import React from "react";
import { Alert, Box, Button, Card, Column, Columns, Stack, Text } from "braid-design-system";
import { useAuth } from "../contexts/AuthProvider";
import { getServerDateTime, getUserSignOnRecords, ServerDateTime, signOffCurrentUser, UserSignOnRecord } from "../services/signOnService";

const SignOff: React.FC = () => {
    const { user } = useAuth();
    const userName = user?.user_name || "-";

    const [serverDateTime, setServerDateTime] = React.useState<ServerDateTime | null>(null);
    const [clientNow, setClientNow] = React.useState(() => new Date());
    const [signOffRecord, setSignOffRecord] = React.useState<UserSignOnRecord | null>(null);
    const [stationId, setStationId] = React.useState<string | null>(null);
    // null while the sign-on record is still loading
    const [isSignedOn, setIsSignedOn] = React.useState<boolean | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    React.useEffect(() => {
        const timer = setInterval(() => setClientNow(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    React.useEffect(() => {
        const fetchServerDateTime = async () => {
            try {
                const dt = await getServerDateTime();
                setServerDateTime(dt);
            } catch {
                // Silently fail - will use fallback from service
            }
        };
        fetchServerDateTime();
    }, []);

    React.useEffect(() => {
        const userNameToCheck = user?.user_name;
        if (!userNameToCheck) {
            return;
        }
        const fetchStationId = async () => {
            try {
                const record = await getUserSignOnRecords(userNameToCheck);
                if (record?.stationid != null) {
                    setStationId(String(record.stationid));
                }
                setIsSignedOn(record?.signon === 1);
            } catch {
                // Silently fail - station ID will show as "-"
                setIsSignedOn(false);
            }
        };
        fetchStationId();
    }, [user?.user_name]);

    const clientDate = React.useMemo(() => {
        const yyyy = String(clientNow.getFullYear());
        const mm = String(clientNow.getMonth() + 1).padStart(2, "0");
        const dd = String(clientNow.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    }, [clientNow]);

    const clientTime = React.useMemo(() => {
        const hh = String(clientNow.getHours()).padStart(2, "0");
        const min = String(clientNow.getMinutes()).padStart(2, "0");
        const ss = String(clientNow.getSeconds()).padStart(2, "0");
        return `${hh}:${min}:${ss}`;
    }, [clientNow]);

    const handleSignOff = async () => {
        setIsLoading(true);
        setErrorMessage(null);
        setSignOffRecord(null);
        try {
            if (!user?.user_name) {
                setErrorMessage("User not found");
                return;
            }
            try {
                const dt = await getServerDateTime();
                setServerDateTime(dt);
            } catch {
                // ignore
            }
            const updated = await signOffCurrentUser(user.user_name);
            if (!updated) {
                setErrorMessage("Unable to sign off user");
                return;
            }
            setSignOffRecord(updated);
            setIsSignedOn(updated.signon === 1);
            if (updated.stationid != null) {
                setStationId(String(updated.stationid));
            }
            window.dispatchEvent(new CustomEvent('webpos:signon-status-changed'));
        } catch (error: any) {
            setErrorMessage(error.message || "Failed to sign off user");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Box padding="medium">
                <Card>
                    <Box padding="medium">
                        <Stack space="medium">
                            <Text weight="strong" size="large">Cashier Sign Off</Text>

                            <Columns space="medium" collapseBelow="tablet">
                                <Column>
                                    <Stack space="xsmall">
                                        <Text tone="secondary">User Name</Text>
                                        <Box background="surface" borderRadius="standard" padding="small">
                                            <div style={{ textAlign: "left" }}>
                                                <Text>{userName}</Text>
                                            </div>
                                        </Box>
                                    </Stack>
                                </Column>

                                <Column>
                                    <Stack space="xsmall">
                                        <Text tone="secondary">Station ID</Text>
                                        <Box background="surface" borderRadius="standard" padding="small">
                                            <div style={{ textAlign: "left" }}>
                                                <Text>{stationId ?? "-"}</Text>
                                            </div>
                                        </Box>
                                    </Stack>
                                </Column>

                                <Column>
                                    <Stack space="xsmall">
                                        <Text tone="secondary">Sign Off Date</Text>
                                        <Box background="surface" borderRadius="standard" padding="small">
                                            <Text>{serverDateTime?.date || clientDate}</Text>
                                        </Box>
                                    </Stack>
                                </Column>

                                <Column>
                                    <Stack space="xsmall">
                                        <Text tone="secondary">Sign Off Time</Text>
                                        <Box background="surface" borderRadius="standard" padding="small">
                                            <Text>{serverDateTime?.time || clientTime}</Text>
                                        </Box>
                                    </Stack>
                                </Column>
                            </Columns>

                            <Box>
                                <div style={{ width: "140px" }}>
                                    <Button
                                        variant="solid"
                                        tone={isSignedOn ? "brandAccent" : "neutral"}
                                        onClick={isSignedOn && !isLoading ? handleSignOff : undefined}
                                        loading={isLoading}
                                    >
                                        Sign Off
                                    </Button>
                                </div>
                            </Box>

                            {isSignedOn === false && signOffRecord === null ? (
                                <Alert tone="info">
                                    <Text>You are not signed on to any counter.</Text>
                                </Alert>
                            ) : null}

                            {errorMessage ? (
                                <Alert tone="critical">
                                    {errorMessage && <Text>{errorMessage}</Text>}
                                </Alert>
                            ) : null}

                            {signOffRecord !== null && signOffRecord.signon === 0 ? (
                                <Alert tone="positive">
                                    <div style={{ textAlign: "left" }}>
                                        <Text>Login user sign off completed</Text>
                                    </div>
                                </Alert>
                            ) : null}
                        </Stack>
                    </Box>
                </Card>
            </Box>
        </>
    );
};

export default SignOff;

